import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, Image as ImageIcon, X } from "lucide-react";
import { getToken, api, ASSET_URL } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => { const t = getToken(); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });

const CATEGORIES = ["Zirkon", "Verblendung", "VMK / NEM", "Brücken", "Inlay / Onlay", "Teleskop & Kombiniert", "Modellguss & Prothetik", "Implantatprothetik", "Schienen & Aufbissbehelfe", "Reparaturen & Zusatzleistungen"];

const empty = { category: "Zirkon", name_de: "", name_en: "", description_de: "", description_en: "", price_from: 0, price_label: "", unit_de: "pro Einheit", unit_en: "per unit", order: 0, active: true, image_media_id: "" };

export default function PricingAdmin() {
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState({ ...empty });
  const [editId, setEditId] = React.useState(null);
  const [filterCat, setFilterCat] = React.useState("");
  const [settings, setSettings] = React.useState(null);
  const [showSettings, setShowSettings] = React.useState(false);
  const [mediaMap, setMediaMap] = React.useState({});
  const imgRef = React.useRef(null);

  const load = () => client.get("/admin/pricing").then(r => setItems(r.data));
  React.useEffect(() => {
    load();
    client.get("/admin/pricing-settings").then(r => setSettings(r.data)).catch(() => {});
    api.media("image").then(l => setMediaMap(Object.fromEntries(l.map(m => [m.id, m])))).catch(() => {});
  }, []);

  const onUploadImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const asset = await api.uploadMedia(file, "image");
      setMediaMap(m => ({ ...m, [asset.id]: asset }));
      setDraft(d => ({ ...d, image_media_id: asset.id }));
      toast.success("Bild gesetzt (automatisch in WebP)");
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { if (imgRef.current) imgRef.current.value = ""; }
  };
  const draftImg = draft.image_media_id ? mediaMap[draft.image_media_id] : null;

  const saveSettings = async () => {
    try { await client.put("/admin/pricing-settings", settings); toast.success("Seitentexte gespeichert"); }
    catch { toast.error("Fehler beim Speichern"); }
  };

  const save = async () => {
    try {
      const body = { ...draft, price_from: parseFloat(draft.price_from) || 0 };
      if (editId) await client.put(`/admin/pricing/${editId}`, body);
      else await client.post("/admin/pricing", body);
      toast.success("Gespeichert"); setDraft({ ...empty }); setEditId(null); load();
    } catch { toast.error("Fehler"); }
  };
  const remove = async (id) => { if (!window.confirm("Position löschen?")) return; await client.delete(`/admin/pricing/${id}`); load(); };
  const edit = (it) => { setDraft({ ...it }); setEditId(it.id); window.scrollTo(0, 0); };

  const filtered = filterCat ? items.filter(i => i.category === filterCat) : items;
  const byCat = filtered.reduce((m, it) => { (m[it.category] = m[it.category] || []).push(it); return m; }, {});

  return (
    <div data-testid="admin-pricing">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><div className="brut-tag mb-3">PREISLISTE</div><h1 className="brut-h2">Preise verwalten</h1></div>
        <button onClick={() => setShowSettings(s => !s)} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="toggle-pricing-settings">
          {showSettings ? "Seitentexte ausblenden" : "Seitentexte der /preisliste bearbeiten"}
        </button>
      </div>

      {showSettings && settings && (
        <div className="admin-card p-5 mb-8 space-y-4" data-testid="pricing-settings-panel">
          <div className="text-xs font-bold uppercase tracking-wider opacity-70">Seitentexte „Preisliste" (öffentlich · CMS-editierbar)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="brut-label">Eyebrow (DE)</label><input className="brut-input" value={settings.eyebrow_de || ""} onChange={e => setSettings({ ...settings, eyebrow_de: e.target.value })} /></div>
            <div><label className="brut-label">Eyebrow (EN)</label><input className="brut-input" value={settings.eyebrow_en || ""} onChange={e => setSettings({ ...settings, eyebrow_en: e.target.value })} /></div>
            <div><label className="brut-label">Titel (DE)</label><input className="brut-input" value={settings.title_de || ""} onChange={e => setSettings({ ...settings, title_de: e.target.value })} /></div>
            <div><label className="brut-label">Titel (EN)</label><input className="brut-input" value={settings.title_en || ""} onChange={e => setSettings({ ...settings, title_en: e.target.value })} /></div>
            <div><label className="brut-label">Intro (DE)</label><textarea rows={4} className="brut-textarea" value={settings.intro_de || ""} onChange={e => setSettings({ ...settings, intro_de: e.target.value })} /></div>
            <div><label className="brut-label">Intro (EN)</label><textarea rows={4} className="brut-textarea" value={settings.intro_en || ""} onChange={e => setSettings({ ...settings, intro_en: e.target.value })} /></div>
            <div><label className="brut-label">CTA-Text (DE)</label><input className="brut-input" value={settings.cta_label_de || ""} onChange={e => setSettings({ ...settings, cta_label_de: e.target.value })} /></div>
            <div><label className="brut-label">CTA-Text (EN)</label><input className="brut-input" value={settings.cta_label_en || ""} onChange={e => setSettings({ ...settings, cta_label_en: e.target.value })} /></div>
            <div><label className="brut-label">CTA-Link</label><input className="brut-input" value={settings.cta_url || ""} onChange={e => setSettings({ ...settings, cta_url: e.target.value })} /></div>
            <div className="hidden md:block" />
            <div><label className="brut-label">Fußnote / Hinweis (DE)</label><textarea rows={3} className="brut-textarea" value={settings.note_de || ""} onChange={e => setSettings({ ...settings, note_de: e.target.value })} /></div>
            <div><label className="brut-label">Fußnote / Hinweis (EN)</label><textarea rows={3} className="brut-textarea" value={settings.note_en || ""} onChange={e => setSettings({ ...settings, note_en: e.target.value })} /></div>
          </div>
          <button onClick={saveSettings} className="brut-btn !py-2 !px-3 text-xs" data-testid="save-pricing-settings"><Save size={12} /> Seitentexte speichern</button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button onClick={() => setFilterCat("")} className={`px-3 py-2 rounded-full text-xs font-bold ${!filterCat ? "bg-[var(--ink)] text-[var(--bg)]" : "admin-card"}`}>Alle ({items.length})</button>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-2 rounded-full text-xs font-bold ${filterCat === c ? "bg-[var(--ink)] text-[var(--bg)]" : "admin-card"}`}>{c}</button>
            ))}
          </div>
          <div className="space-y-4">
            {Object.entries(byCat).map(([cat, list]) => (
              <div key={cat}>
                <div className="text-xs font-bold uppercase tracking-wider opacity-60 mb-2">{cat}</div>
                <div className="space-y-2">
                  {list.map(it => (
                    <div key={it.id} className="admin-card p-4 flex items-start justify-between gap-4" data-testid={`price-row-${it.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-base">{it.name_de}</div>
                        <div className="text-sm opacity-70">{it.description_de}</div>
                        <div className="text-xs opacity-50 mt-1">{it.unit_de}</div>
                      </div>
                      <div className="text-right shrink-0">
                        {it.price_label ? <div className="font-bold text-sm">{it.price_label}</div> : <div className="font-extrabold text-xl whitespace-nowrap">{Number(it.price_from).toFixed(0)} €</div>}
                        <div className="flex gap-2 mt-2 justify-end">
                          <button onClick={() => edit(it)} className="text-xs font-bold underline">Edit</button>
                          <button onClick={() => remove(it.id)} className="text-xs font-bold underline text-red-600">Del</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-12 md:col-span-5">
          <div className="admin-card p-5 space-y-3 sticky top-6">
            <div className="text-xs font-bold uppercase tracking-wider opacity-70">{editId ? "Position bearbeiten" : "Neue Position"}</div>
            <div>
              <label className="brut-label">Kategorie</label>
              <select className="brut-select" value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="brut-label">Bezeichnung (DE)</label>
              <input className="brut-input" value={draft.name_de} onChange={e => setDraft({ ...draft, name_de: e.target.value })} />
            </div>
            <div>
              <label className="brut-label">Beschreibung (DE)</label>
              <textarea rows={3} className="brut-textarea" value={draft.description_de} onChange={e => setDraft({ ...draft, description_de: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="brut-label">Preis ab (€)</label>
                <input type="number" min="0" step="1" className="brut-input" value={draft.price_from} onChange={e => setDraft({ ...draft, price_from: e.target.value })} />
              </div>
              <div>
                <label className="brut-label">Einheit (DE)</label>
                <input className="brut-input" value={draft.unit_de} onChange={e => setDraft({ ...draft, unit_de: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="brut-label">Preis-Label (alternativ, z.B. „auf Anfrage")</label>
              <input className="brut-input" placeholder="leer lassen wenn fixer Preis" value={draft.price_label} onChange={e => setDraft({ ...draft, price_label: e.target.value })} />
            </div>
            <div>
              <label className="brut-label">Bezeichnung (EN)</label>
              <input className="brut-input" value={draft.name_en} onChange={e => setDraft({ ...draft, name_en: e.target.value })} />
            </div>
            <div>
              <label className="brut-label">Description (EN)</label>
              <textarea rows={2} className="brut-textarea" value={draft.description_en} onChange={e => setDraft({ ...draft, description_en: e.target.value })} />
            </div>
            <div>
              <label className="brut-label">Bild (optional)</label>
              {draftImg ? (
                <div className="flex items-center gap-3">
                  <img src={ASSET_URL(draftImg.variants?.thumb || draftImg.variants?.webp || draftImg.variants?.original)} alt="" className="w-20 h-14 object-cover border border-[var(--ink)] rounded-lg" />
                  <button onClick={() => setDraft({ ...draft, image_media_id: "" })} className="font-mono text-[11px] underline text-red-600 inline-flex items-center gap-1" data-testid="pricing-clear-image"><X size={11} /> Entfernen</button>
                </div>
              ) : (
                <button onClick={() => imgRef.current?.click()} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="pricing-upload-image"><ImageIcon size={12} /> Bild hochladen</button>
              )}
              <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={onUploadImg} data-testid="pricing-image-input" />
              <p className="text-[10px] opacity-60 mt-1">JPG/PNG — wird automatisch in WebP umgewandelt.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="brut-btn !py-2 !px-3 text-xs"><Save size={12}/> Speichern</button>
              {editId && <button onClick={() => { setDraft({ ...empty }); setEditId(null); }} className="text-xs font-bold underline">Abbrechen</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
